/**
 * Sticky top bar (spec.md § Top bar): 57px tall, translucent + blurred.
 *
 *   Left:  `S` glyph mark + "scratchpad" wordmark + monospaced slug (/p/slug).
 *   Right: live expiry indicator, a divider, the Save button, the `…` menu.
 *
 * The bar is layout + chrome only. Save state comes from the editor (Slice B)
 * via the route; the `…` menu actions are wired by the route to the api client.
 */

import { ActionsMenu } from "./ActionsMenu";
import type { ActionsMenuProps } from "./ActionsMenu";
import { ExpiryIndicator } from "./ExpiryIndicator";
import { SaveButton } from "./SaveButton";
import type { SaveState } from "./padEditorContract";

export interface TopBarProps {
  slug: string;
  expiresAt: string;
  saveState: SaveState;
  onSave: () => void;
  menuActions: ActionsMenuProps;
}

export function TopBar({
  slug,
  expiresAt,
  saveState,
  onSave,
  menuActions,
}: TopBarProps): React.JSX.Element {
  return (
    <header className="sticky top-0 z-40 h-[57px] border-b border-line bg-bg/80 backdrop-blur-md">
      <div className="mx-auto flex h-full max-w-[1100px] items-center justify-between px-5">
        <div className="flex items-center gap-3">
          <span
            aria-hidden="true"
            className="flex size-7 items-center justify-center rounded-[7px] bg-primary font-mono text-[15px] font-semibold text-primary-fg"
          >
            S
          </span>
          <span className="font-mono text-[14px] font-medium text-text">scratchpad</span>
          <span className="font-mono text-[13px] text-text-3">/p/{slug}</span>
        </div>
        <div className="flex items-center gap-3">
          <ExpiryIndicator expiresAt={expiresAt} />
          <span aria-hidden="true" className="h-5 w-px bg-line" />
          <SaveButton state={saveState} onSave={onSave} />
          <ActionsMenu {...menuActions} />
        </div>
      </div>
    </header>
  );
}
